<template>
  <v-card class="ma-0 printouts">
    <v-toolbar color="primary" class="ma-0" dense dark flat>
      <v-card-title class="pa-0">Printouts</v-card-title>
    </v-toolbar>
    <div class="content pa-2 space-y-2.5">
      <div class="space-x-2.5">
        <v-btn color="primary" dark @click="getSalesPrintout">
          <div class="d-flex align-center space-x-1">
            <v-icon>mdi-file</v-icon>
            <span>Sales</span>
            <v-progress-circular
              v-if="fetching"
              indeterminate
              :size="15"
            ></v-progress-circular>
          </div>
        </v-btn>
        <v-btn color="primary" dark @click="getExpensesPrintout">
          <div class="d-flex align-center space-x-1">
            <v-icon>mdi-file</v-icon>
            <span>Expenses</span>
            <v-progress-circular
              v-if="fetching"
              indeterminate
              :size="15"
            ></v-progress-circular>
          </div>
        </v-btn>
        <v-btn color="primary" dark @click="getLoansPrintout">
          <div class="d-flex align-center space-x-1">
            <v-icon>mdi-file</v-icon>
            <span>Credits</span>
            <v-progress-circular
              v-if="fetching"
              indeterminate
              :size="15"
            ></v-progress-circular>
          </div>
        </v-btn>
        <v-btn color="primary" dark @click="getInventoryOverallReport">
          <div class="d-flex align-center space-x-1">
            <v-icon>mdi-file</v-icon>
            <span>Inventory</span>
            <v-progress-circular
              v-if="fetching"
              indeterminate
              :size="15"
            ></v-progress-circular>
          </div>
        </v-btn>

        <v-btn
          v-if="authPermissions.has('sales:insight_reports')"
          color="primary"
          dark
          @click="$router.push('/printouts/sales-report')"
        >
          <div class="d-flex align-center space-x-1">
            <v-icon>mdi-file</v-icon>
            <span>Daily Sales Insight</span>
          </div>
        </v-btn>
      </div>
      <div class="controls">
        <div class="space-y-2.5">
          <div class="d-flex space-x-2.5">
            <v-text-field
              :error-messages="errors.dateStart"
              v-model="dateStart"
              type="date"
              outlined
              dense
              label="Start"
              hide-details="auto"
            />
            <v-text-field
              :error-messages="errors.dateEnd"
              v-model="dateEnd"
              type="date"
              outlined
              dense
              label="End"
              hide-details="auto"
            />
          </div>
          <v-select
            v-if="user.role === 'admin'"
            label="Stores"
            :items="stores"
            v-model="storeId"
            outlined
            dense
            item-value="id"
            item-text="name"
            hide-details="auto"
          ></v-select>
          <v-select
            outlined
            dense
            hide-details="auto"
            label="Interval"
            :error-messages="errors.interval"
            v-model="interval"
            :items="['custom', 'day', 'week', 'month', 'year']"
          ></v-select>
        </div>
      </div>
    </div>
  </v-card>
</template>
<style scoped lang="scss">
.printouts {
  display: flex;
  flex-direction: column;

  .content {
    display: flex;
    flex-direction: column;
  }

  .controls {
    display: flex;
    flex-direction: column;
  }
}
</style>
<script>
import Axios from '@/extra-functions/axios.extra'
import { mapState } from 'vuex'
import SaleSummary from '@/pdf/sale-summary.pdf'
import {
  formatAccounting,
  getCurrentDateEnUsFormat,
  toPercent,
  getCurrentDate,
} from '@/lib/utils'
import LoanReport from '../../../../pdf/loan-reports.pdf'
import ExpenseReport from '../../../../pdf/expense-reports.pdf'
import OverallInventory from '../../../../pdf/overall-inventory.pdf'
import { ErrorPopup, LoadingPopup } from '../../../../lib/utils'
import { isAxiosError } from 'axios'

const defaultErrors = () => ({
  dateStart: [],
  dateEnd: [],
  interval: [],
})

export default {
  name: 'Printouts',
  data() {
    return {
      interval: 'custom',
      dateStart: '',
      dateEnd: getCurrentDate(),
      fetching: false,
      storeId: '',
      errors: defaultErrors(),
    }
  },
  created() {
    this.storeId = this.userStoreId
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      token: (state) => state.auth.token,
      userStoreId: (state) => state.settings.storeId,
      stores: (state) => state.branchLocation.list,
      authPermissions: (state) => state.auth.userPermissions,
    }),
  },
  watch: {
    interval(val) {
      if (val === 'custom') return
      if (val === 'week') {
        const date = new Date(this.dateEnd || new Date())
        date.setDate(date.getDate() - 7)
        this.dateStart = getCurrentDate(date)
        return
      }
      if (val === 'month') {
        const date = new Date(this.dateEnd)
        date.setMonth(date.getMonth() - 1)
        this.dateStart = getCurrentDate(date)
        return
      }
      if (val === 'year') {
        const date = new Date(this.dateEnd || new Date())
        date.setFullYear(date.getFullYear() - 1)
        this.dateStart = getCurrentDate(date)
        return
      }
      const date = new Date(this.dateEnd || new Date())
      date.setDate(date.getDate() - 1)
      this.dateStart = getCurrentDate(date)
    },
  },
  methods: {
    async getSalesPrintout() {
      try {
        LoadingPopup.fire({
          text: 'Getting Sales Report',
        })
        this.errors = defaultErrors()
        const axios = Axios(this.token)
        if (this.fetching) return
        this.fetching = true
        const { data } = await axios.get('/stores/sales-report', {
          params: {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            storeId: this.storeId,
          },
        })
        const pdf = new SaleSummary()
        const items = data.items.map((item) => {
          const profitColor = item.profit < 0 ? [179, 11, 0] : [16, 124, 16]
          return [
            {
              content: item.item,
              styles: {
                ...item?.settings,
              },
            },
            { content: item.units_sold, styles: { halign: 'center' } },
            {
              content: formatAccounting(item.current_srp),
              styles: { halign: 'right' },
            },
            {
              content: formatAccounting(item.current_cost),
              styles: { halign: 'right' },
            },
            {
              content: formatAccounting(item.revenue),
              styles: { halign: 'right' },
            },
            // { content: formatAccounting(item.profit), styles: { halign: 'right', textColor: profitColor } },
            {
              content: item.discount_rate,
              styles: { halign: 'center', textColor: profitColor },
            },
          ]
        })
        const brandTypeAmounts = Object.keys(data.brand_type_amounts).map(
          (key) => ({
            amount: formatAccounting(data.brand_type_amounts[key]?.amount ?? 0),
            name: data.brand_type_amounts[key]?.name ?? '',
          }),
        )

        LoadingPopup.close()
        pdf.generate(
          {
            storeName: data.store,
            dateRange: `${getCurrentDateEnUsFormat(
              new Date(data.date_start),
            )} - ${getCurrentDateEnUsFormat(new Date(data.date_end))}`,
            grossRevenue: formatAccounting(data.gross_revenue),
            totalCost: formatAccounting(data.total_cost),
            grossProfit: formatAccounting(data.gross_profit),
            grossProfitMargin: toPercent(data.gross_profit_margin),
            brandTypeAmounts,
          },
          items,
        )
      } catch (e) {
        if (isAxiosError(e)) {
          const {
            status,
            data: { errors },
          } = e?.response
          if (status === 422) {
            LoadingPopup.close()
            this.errors = errors
          }
        }
        console.error(e.message)
        ErrorPopup.fire()
      } finally {
        this.fetching = false
      }
    },
    async getExpensesPrintout() {
      try {
        this.errors = defaultErrors()
        const axios = Axios(this.token)
        if (this.fetching) return
        this.fetching = true
        const { data } = await axios.get('/stores/expenses-report', {
          params: {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            storeId: this.storeId,
          },
        })
        const expenseReport = new ExpenseReport()
        const items = data.expenses.map((item) => {
          return [
            { content: item.category, styles: { halign: 'center' } },
            { content: item.description },
            {
              content: getCurrentDate(new Date(item.date), 'en-US'),
              styles: { halign: 'center' },
            },
            {
              content: formatAccounting(item.cost),
              styles: { halign: 'right' },
            },
          ]
        })
        expenseReport.generate(
          {
            storeName: data.store,
            avgDailyExpense: formatAccounting(data.avg_daily_expense),
            dateRange: `${getCurrentDate(
              new Date(data.date_start),
              'en-US',
            )} - ${getCurrentDate(new Date(data.date_end), 'en-US')}`,
            totalExpenses: formatAccounting(data.total_expenses),
          },
          items,
        )
      } catch (e) {
        if (!e.response) {
          console.error(e.message)
          return
        }
        const {
          status,
          data: { errors },
        } = e.response
        if (status === 422) {
          this.errors = errors
        }
      } finally {
        this.fetching = false
      }
    },
    async getLoansPrintout() {
      try {
        this.errors = defaultErrors()
        const axios = Axios(this.token)
        if (this.fetching) return
        this.fetching = true
        const { data } = await axios.get('/stores/loans-report', {
          params: {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            storeId: this.storeId,
          },
        })

        const dateDueRemarkColor = (remarks = '') => {
          if (remarks === 'overdue') return [255, 0, 0]
          if (remarks === 'due today') return [255, 255, 0]
          if (remarks === 'almost due') return [255, 165, 0]
          if (remarks === 'paid') return [0, 128, 0]
          return [0, 0, 255]
        }

        const items = data.items.map((item) => {
          return [
            {
              content: item.receipt_no,
              styles: { halign: 'center', fontSize: 8 },
            },
            { content: item.name, styles: { fontSize: 8 } },
            {
              content: getCurrentDate(new Date(item.date_due), 'en-US'),
              styles: { fontSize: 8, halign: 'center' },
            },
            {
              content: getCurrentDate(new Date(item.date_loaned), 'en-US'),
              styles: { fontSize: 8, halign: 'center' },
            },
            {
              content: formatAccounting(item?.accounts_receivable),
              styles: { fontSize: 8, halign: 'right' },
            },
            {
              content: formatAccounting(item?.balance),
              styles: { fontSize: 8, halign: 'right' },
            },
            {
              content: formatAccounting(item?.amount_paid),
              styles: { fontSize: 8, halign: 'right' },
            },
            {
              content: item?.remarks,
              styles: {
                fontSize: 8,
                halign: 'right',
                textColor: dateDueRemarkColor(item?.remarks),
              },
            },
          ]
        })

        const loanReport = new LoanReport()
        loanReport.generate(
          {
            storeName: data?.store ?? '',
            dateRange: `${getCurrentDate(
              new Date(data?.date_start),
              'en-US',
            )} - ${getCurrentDate(new Date(data?.date_end), 'en-US')}`,
            totalAmount: formatAccounting(data?.total_amount),
            balance: formatAccounting(data?.total_balance),
            totalPaid: formatAccounting(data?.total_paid),
            avgDailyExpense: formatAccounting(data?.avg_daily_expense),
          },
          items,
        )
      } catch (e) {
        if (!e.response) {
          console.error(e.message)
          return
        }
        const {
          status = 500,
          data: { errors },
        } = e?.response
        if (status === 422) {
          this.errors = errors
        }
      } finally {
        this.fetching = false
      }
    },
    async getInventoryOverallReport() {
      try {
        this.errors = defaultErrors()
        const axios = Axios(this.token)
        if (this.fetching) return
        this.fetching = true
        const { data } = await axios.get(
          '/inventories-v2/store-overall-report',
          {
            params: {
              dateStart: this.dateStart,
              dateEnd: this.dateEnd,
              storeId: this.storeId,
            },
          },
        )
        const overallData = data.map((item) => {
          const textColor = item.out <= 0 ? '#DC2626' : '#000000'
          return [
            { content: item.item, styles: { textColor } },
            { content: item.in, styles: { halign: 'center', textColor } },
            { content: item.out, styles: { halign: 'center', textColor } },
            {
              content: item.remain,
              styles: {
                halign: 'center',
                textColor,
              },
            },
            { content: item.unit, styles: { halign: 'center', textColor } },
            {
              content: formatAccounting(item.worth),
              styles: {
                halign: 'center',
                textColor,
              },
            },
          ]
        })
        const dateRange = () => {
          const dates = [this.dateStart, this.dateEnd]
            .filter((item) => item)
            .map((item) => getCurrentDate(new Date(item), 'en-US'))
          return dates.join(' - ')
        }
        const overallInventory = new OverallInventory()
        overallInventory.generate(overallData, dateRange())
      } catch (e) {
        if (!e.response) {
          console.error(e.message)
          return
        }
        const {
          status = 500,
          data: { errors },
        } = e?.response
        if (status === 422) {
          this.errors = errors
        }
      } finally {
        this.fetching = false
      }
    },
  },
}
</script>
